/**
 * A project's Builds view (app-clarity `builds`, WP-7): one sentence instead of KPI cards, the
 * release channels as rows (where each one stands; the stepper only while a build is in flight),
 * landings that need attention, and what landed on main with the builds that shipped it.
 * Every number drills: build numbers open the full build card, counts jump to their rows.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { StatusIcon, type StatusKind } from '@viko/transcript-ui'
import { useState } from 'react'
import { BuildView, buildNumber, FeedbackSheet } from '../components/HubCards.js'
import { Ago, errorText, SkeletonRows, useToast } from '../components/ui.js'
import { useBackend } from '../lib/backend.js'
import { runHref } from '../lib/ia.js'
import { useBuilds, useLandings } from '../lib/queries.js'
import type { BuildCard, Landing, Project } from '../lib/types.js'
import { feedbackToBuildMs, formatDur, landToBuildMs } from '../lib/work.js'
import './project-views.css'

const WEEK = 7 * 24 * 3_600_000
const STEPS = ['queued', 'building', 'uploading', 'processing', 'available'] as const
const IN_FLIGHT = new Set(['queued', 'building', 'uploading', 'processing'])

export type ChannelLane = {
  id: string
  name?: string
  target?: string
  production?: boolean
  machine?: string
}

/** Where one release channel stands, from its newest builds (pure, unit-tested). */
export function channelState(builds: BuildCard[]): {
  kind: StatusKind
  inFlight?: BuildCard
  latest?: BuildCard
  text: string
} {
  const sorted = [...builds].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
  const inFlight = sorted.find((b) => IN_FLIGHT.has(b.status))
  const latest = sorted.find((b) => !IN_FLIGHT.has(b.status))
  if (inFlight) {
    const step = STEPS.indexOf(inFlight.status as (typeof STEPS)[number]) + 1
    return {
      kind: 'run',
      inFlight,
      ...(latest ? { latest } : {}),
      text: `build ${buildNumber(inFlight)} · ${inFlight.status}, step ${Math.max(step, 1)} of ${STEPS.length}`,
    }
  }
  if (!latest) return { kind: 'idle', text: 'no builds yet' }
  if (latest.status === 'failed')
    return { kind: 'fail', latest, text: `build ${buildNumber(latest)} failed` }
  if (latest.status === 'cancelled')
    return { kind: 'idle', latest, text: `build ${buildNumber(latest)} cancelled` }
  return { kind: 'ok', latest, text: `build ${buildNumber(latest)} available` }
}

/** The builds that shipped a landing: by its tasks, or by the build's head commit. */
export function buildsWithLanding(l: Landing, builds: BuildCard[]): number[] {
  return builds
    .filter(
      (b) =>
        b.status !== 'failed' &&
        ((l.commit && b.headCommit === l.commit) ||
          b.taskIds.some((t) => l.taskIds.includes(t)) ||
          b.tasks.some((t) => t.taskId && l.taskIds.includes(t.taskId))),
    )
    .map(buildNumber)
    .sort((a, b) => a - b)
}

/** Policy as meta words (no chips): who gets the build and what starting one asks for. */
export function channelAudience(l: ChannelLane): string {
  if (l.production) return 'production · asks for your passkey'
  if (l.target === 'testflight') return 'internal testers'
  if (l.target === 'web') return 'preview link'
  return 'internal'
}

function StartBuild({
  projectId,
  lane,
  machine,
}: {
  projectId: string
  lane: string
  machine: string
}) {
  const b = useBackend()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  return (
    <button
      type="button"
      className="pv-btn"
      disabled={busy}
      onClick={async () => {
        setBusy(true)
        try {
          await b.dispatch(
            'land.deploy',
            machine,
            { projectId, lane },
            { summary: `Build ${lane}` },
          )
          toast('Build started')
        } catch (e) {
          toast(errorText(e), 'error')
        } finally {
          setBusy(false)
        }
      }}
    >
      {busy ? 'Starting…' : 'Build now'}
    </button>
  )
}

function ChannelRow({
  project,
  lane,
  builds,
  onFeedback,
}: {
  project: Project
  lane: ChannelLane
  builds: BuildCard[]
  onFeedback: (c: BuildCard) => void
}) {
  const st = channelState(builds)
  const [open, setOpen] = useState<string | null>(null)
  const name = lane.name ?? lane.id
  const machine = lane.machine ?? project.config?.machines.allowed[0]
  const shown = open ? builds.find((b) => b.buildId === open) : undefined
  const drill = (c: BuildCard | undefined, label: string) =>
    c ? (
      <button
        type="button"
        className="pv-link"
        aria-expanded={open === c.buildId}
        onClick={() => setOpen(open === c.buildId ? null : c.buildId)}
      >
        {label}
      </button>
    ) : (
      label
    )
  const head = st.inFlight ?? st.latest
  const [first, ...rest] = st.text.split(' · ')
  return (
    <li>
      <div className={`pv-row pv-row--stack${st.inFlight ? ' is-live' : ''}`}>
        <span className="pv-row__icon">
          <StatusIcon kind={st.kind} label={false} />
        </span>
        <span className="pv-row__main">
          <span className="pv-row__title">
            <strong>{name}</strong>
            <span className="pv-sub">
              {' · '}
              {head ? drill(head, first ?? '') : first}
              {rest.length ? ` · ${rest.join(' · ')}` : ''}
            </span>
          </span>
          <span className="pv-row__meta">
            {st.inFlight ? (
              st.inFlight.tasks.length ? (
                `Includes ${st.inFlight.tasks.map((t) => t.title).join(', ')}`
              ) : (
                channelAudience(lane)
              )
            ) : st.latest?.status === 'failed' ? (
              <>
                <span className="pv-word-fail">Failed</span>
                {st.latest.failure?.category ? ` at ${st.latest.failure.category}` : ''}
                {st.latest.failure?.cause ? ` · ${st.latest.failure.cause}` : ''}
              </>
            ) : (
              <>
                {st.latest?.live ? (
                  <>
                    <span className="pv-word-ok">live</span>
                    {st.latest.live.url ? (
                      <>
                        {' on '}
                        <a
                          className="pv-link"
                          href={st.latest.live.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {st.latest.live.environment}
                        </a>
                      </>
                    ) : (
                      ` on ${st.latest.live.environment}`
                    )}
                    {' · '}
                  </>
                ) : null}
                {channelAudience(lane)}
                {st.latest ? (
                  <>
                    {' · '}
                    <Ago ts={st.latest.updatedAt ?? st.latest.createdAt ?? undefined} />
                    {' · '}
                    <button
                      type="button"
                      className="pv-link"
                      onClick={() => st.latest && onFeedback(st.latest)}
                    >
                      Feedback on this build
                    </button>
                  </>
                ) : null}
              </>
            )}
          </span>
        </span>
        <span className="pv-row__aside">
          {st.inFlight ? (
            <>
              started <Ago ts={st.inFlight.createdAt ?? undefined} />
            </>
          ) : machine ? (
            <StartBuild projectId={project.id} lane={lane.id} machine={machine} />
          ) : null}
        </span>
        {shown ? (
          <div className="pv-row__detail">
            <BuildView card={shown} onFeedback={() => onFeedback(shown)} />
          </div>
        ) : null}
      </div>
    </li>
  )
}

function landingKind(state: string): StatusKind {
  return state === 'failed'
    ? 'fail'
    : state === 'awaiting_approval'
      ? 'wait'
      : state === 'queued'
        ? 'idle'
        : state === 'landed'
          ? 'ok'
          : 'run'
}
const LANDING_WORD: Record<string, [string, string]> = {
  failed: ['failed', 'pv-word-fail'],
  awaiting_approval: ['needs you', 'pv-word-wait'],
  queued: ['queued', ''],
  running: ['landing', 'pv-word-run'],
  cancelled: ['cancelled', ''],
}

function LandingRow({ l, projectId }: { l: Landing; projectId: string }) {
  const [word, cls] = LANDING_WORD[l.state] ?? [l.state.replace('_', ' '), '']
  const title = l.summary ?? l.branch ?? l.id
  return (
    <li>
      <div className="pv-row">
        <span className="pv-row__icon">
          <StatusIcon kind={landingKind(l.state)} label={false} />
        </span>
        <span className="pv-row__main">
          <span className="pv-row__title">
            {l.runId ? (
              <Link className="pv-link" to={runHref(l.runId, projectId)}>
                {title}
              </Link>
            ) : (
              title
            )}
          </span>
          <span className="pv-row__meta">
            <span className={cls}>{word}</span>
            {l.reason || l.error ? ` · ${l.reason ?? l.error}` : l.step ? ` · ${l.step}` : ''}
            {l.branch ? (
              <>
                {' · '}
                <code className="pv-mono">{l.branch}</code>
              </>
            ) : null}
          </span>
        </span>
        <span className="pv-row__aside">
          <Ago ts={l.updatedAt} />
        </span>
      </div>
    </li>
  )
}

function LandedRow({ l, builds }: { l: Landing; builds: BuildCard[] }) {
  const inBuilds = buildsWithLanding(l, builds)
  return (
    <li>
      <div className="pv-row">
        <span className="pv-row__icon">
          <StatusIcon kind="ok" label={false} />
        </span>
        <span className="pv-row__main">
          <span className="pv-row__title">{l.summary ?? l.branch ?? l.id}</span>
          <span className="pv-row__meta">
            {l.commit ? (
              <code className="pv-mono" title={l.commit}>
                {l.commit.slice(0, 7)}
              </code>
            ) : (
              'no commit recorded'
            )}
            {inBuilds.length ? ` · in build ${inBuilds.join(', ')}` : ' · not in a build yet'}
          </span>
        </span>
        <span className="pv-row__aside">
          <Ago ts={l.updatedAt} />
        </span>
      </div>
    </li>
  )
}

export function ProjectBuilds({ project }: { project: Project }) {
  const builds = useBuilds(project.id)
  const landings = useLandings(project.id)
  const b = useBackend()
  const tasks = useQuery({
    queryKey: ['tasks', project.id, 'all'],
    queryFn: () => b.api.tasks({ projectId: project.id }),
    retry: false,
  })
  const [feedback, setFeedback] = useState<BuildCard | null>(null)
  const list = builds.data ?? []
  const all = landings.data ?? []
  const lanes: ChannelLane[] = project.config?.release?.lanes ?? []
  // Builds on a lane that isn't configured (any more) still show as a channel.
  const channels: ChannelLane[] = [
    ...lanes,
    ...[...new Set(list.map((c) => c.laneId))]
      .filter((id) => !lanes.some((l) => l.id === id))
      .map((id) => ({ id })),
  ]
  const now = Date.now()
  const week = list.filter((c) => (c.createdAt ?? 0) > now - WEEK)
  const failed = week.filter((c) => c.status === 'failed')
  const fb = feedbackToBuildMs(list, tasks.data ?? [])
  const lb = landToBuildMs(list, all)
  const landed = all.filter((l) => l.state === 'landed').sort((a, b) => b.updatedAt - a.updatedAt)
  const landedWeek = landed.filter((l) => l.updatedAt > now - WEEK).length
  const attention = all
    .filter((l) => l.state !== 'landed' && l.state !== 'cancelled')
    .sort((a, b) => b.updatedAt - a.updatedAt)
  const jump = (id: string) => () =>
    document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  return (
    <div className="pv-body">
      {builds.isError ? (
        <p className="pv-empty">Builds didn’t load: {errorText(builds.error)}</p>
      ) : builds.isPending ? (
        <SkeletonRows n={3} />
      ) : (
        <p className="pv-sentence" data-tap-ok>
          {list.length ? (
            <>
              <button type="button" className="pv-link" onClick={jump('pv-channels')}>
                {week.length} build{week.length === 1 ? '' : 's'} this week
              </button>
              {' · '}
              {failed.length ? (
                <button
                  type="button"
                  className="pv-link pv-word-fail"
                  onClick={jump('pv-channels')}
                >
                  {failed.length} failed
                </button>
              ) : (
                'none failed'
              )}
              {fb !== null ? (
                <>
                  {' · feedback reaches a build in '}
                  <span
                    className="pv-link"
                    title="From feedback on a build to the next build that includes the fix, averaged"
                  >
                    {formatDur(fb)}
                  </span>
                  {' on average'}
                </>
              ) : null}
              {lb !== null ? (
                <>
                  {' · a landing reaches a build in '}
                  <span className="pv-link" title="From landing on main to the first build with it">
                    {formatDur(lb)}
                  </span>
                </>
              ) : null}
            </>
          ) : (
            'No builds yet. A landing starts one on each release channel that builds on land.'
          )}
        </p>
      )}

      <section className="pv-sec" aria-labelledby="pv-channels">
        <h2 className="pv-h" id="pv-channels">
          Release channels <span className="pv-h__count">{channels.length}</span>
        </h2>
        {channels.length ? (
          <ul className="pv-rows" aria-label="Release channels">
            {channels.map((l) => (
              <ChannelRow
                key={l.id}
                project={project}
                lane={l}
                builds={list.filter((c) => c.laneId === l.id)}
                onFeedback={setFeedback}
              />
            ))}
          </ul>
        ) : (
          <p className="pv-empty">
            No release channels. Add one under <code>release.lanes</code> in this project’s
            configuration.
          </p>
        )}
      </section>

      {attention.length ? (
        <section className="pv-sec" aria-labelledby="pv-landing">
          <h2 className="pv-h" id="pv-landing">
            Landing <span className="pv-h__count">{attention.length}</span>
          </h2>
          <ul className="pv-rows" aria-label="Landings in progress">
            {attention.map((l) => (
              <LandingRow key={l.id} l={l} projectId={project.id} />
            ))}
          </ul>
        </section>
      ) : null}

      <section className="pv-sec" aria-labelledby="pv-landed">
        <h2 className="pv-h" id="pv-landed">
          Landed on main{' '}
          {landed.length ? <span className="pv-h__count">{landedWeek} this week</span> : null}
        </h2>
        {landings.isPending ? (
          <SkeletonRows n={2} />
        ) : landed.length ? (
          <ul className="pv-rows" aria-label="Landed on main">
            {landed.slice(0, 20).map((l) => (
              <LandedRow key={l.id} l={l} builds={list} />
            ))}
          </ul>
        ) : (
          <p className="pv-empty">Nothing landed yet. Landed work shows here with its commit.</p>
        )}
      </section>
      {feedback ? <FeedbackSheet build={feedback} onClose={() => setFeedback(null)} /> : null}
    </div>
  )
}
